import userService from "@/endpoints/userService";
import { adoptBrandSetIntoStore } from "@/components/BrandSets/shared/adoptBrandSet";
import { updateBrand } from "@/store/slices/brands/brandsSlice";
import type { BrandSetView } from "@/components/BrandSets/shared/brandSet.types";
import type { AppDispatch, RootState } from "@/store/store";

/**
 * Re-sync brand sets from the server into `activeGroup.brands`.
 *
 * The assistant can create, clone and edit brand sets on the user's behalf, and
 * those writes happen server-side — the store only learns about them when told.
 * This runs on the `brand_sets_changed` socket event so the brand dropdown shows
 * a set the assistant just made, without a page reload.
 *
 * Existing sets are patched in place (a rename or membership edit must not lose
 * the entity's position in the brand order); new ones go through the same
 * `adoptBrandSetIntoStore` path the create flow uses. Best-effort: a failed
 * refresh leaves the store as it was.
 *
 * Deletions are not handled here: the list omits deleted sets rather than
 * flagging them, and it is paginated, so absence cannot be read as removal.
 */
export const refreshBrandSets =
  () =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    let views: BrandSetView[] = [];
    try {
      const raw = await userService.brandSets.list();
      // config.ts strips one `.data` layer, so the list arrives as
      // { pageInfo, data: [BrandSetView] } — or a bare array.
      views = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    } catch {
      return;
    }
    if (!views.length) return;

    const state = getState();
    const brandEntities = state.activeGroup.brands.entities as Record<
      number | string,
      any
    >;

    for (const view of views) {
      if (!view?.ulid) continue;

      if (brandEntities[view.ulid]) {
        dispatch(
          updateBrand({
            id: view.ulid,
            changes: {
              name: view.name,
              displayName: view.name,
              color: view.color ?? null,
              description: view.description ?? "",
              weighted: view.weighted ?? false,
              marketIds:
                view.status === "DISABLED"
                  ? []
                  : (view.markets ?? []).map((mk) => mk.marketId),
              members: (view.members ?? []).map((m) => ({
                id: m.id,
                weight: m.weight ?? 1,
                logo: m.logo ?? brandEntities[m.id]?.logo ?? null,
                name:
                  m.displayName ||
                  m.name ||
                  brandEntities[m.id]?.displayName ||
                  brandEntities[m.id]?.name ||
                  "",
              })),
            },
          }),
        );
        continue;
      }

      // Each adopt overwrites the market's order, so re-read rather than reuse.
      const latestMapping = (getState().activeGroup.brandsOrderMapping ?? {}) as Record<
        string,
        (number | string)[]
      >;
      adoptBrandSetIntoStore(view, {
        dispatch,
        brandsOrderMapping: latestMapping,
        brandEntities,
      });
    }
  };

export default refreshBrandSets;
